import type { DocumentSearchParams } from '@api/types/document';
import { stableFilters } from './_filters';

type Id = string | number;

// Root / leaf segments exported so consumers that compare by segment (e.g. the socket
// backlinks predicate in useSyncAuditData) reference one source of truth, not bare strings.
export const DOCUMENT_DETAIL_ROOT = 'document';
export const DOCUMENT_EDITOR_SEGMENT = 'editor';

/**
 * Document query keys (Server-state rebuild, P3).
 *
 * Deliberately keeps TWO roots, matching today's values exactly (zero behaviour change):
 *   - `documents` (plural) — the audit's document LIST and list-scoped resources.
 *   - `document`  (singular) — a single document's DETAIL / editor / annotation history.
 *
 * They are NOT merged into one cascade on purpose: the socket backlinks predicate in
 * `useSyncAuditData` keys off `key[0] === 'document'`, and `useCollaborationSync` targets
 * the editor vs the list independently. A merge would broaden refetch fanout and force
 * those carefully-tuned boundaries to change for no real gain. The win here is killing the
 * magic-string suffixes (`[...QK.documents(id), 'meta-types']`) and centralizing, not
 * reshaping the invalidation graph.
 */
export const documentKeys = {
  // ── list root (plural) ──────────────────────────────────────────────────
  lists: (auditId?: string) => ['documents', auditId] as const,
  list: (auditId?: string, params?: DocumentSearchParams) => {
    const filters = stableFilters(params);
    return filters ? (['documents', auditId, filters] as const) : (['documents', auditId] as const);
  },
  metaTypes: (auditId?: string) => [...documentKeys.lists(auditId), 'meta-types'] as const,
  deletedList: (auditId?: string, params?: DocumentSearchParams) =>
    ['documents', 'deleted', auditId, params] as const,

  // ── detail root (singular) ──────────────────────────────────────────────
  detail: (auditId: string, documentId: Id) =>
    [DOCUMENT_DETAIL_ROOT, auditId, String(documentId)] as const,
  editor: (auditId: string, documentId: Id) =>
    [...documentKeys.detail(auditId, documentId), DOCUMENT_EDITOR_SEGMENT] as const,
  annotationHistory: (auditId: string, documentId: Id) =>
    [...documentKeys.detail(auditId, documentId), 'annotation-history'] as const,

  // ── mutation keys (the mutation cache, never a query) ───────────────────
  /**
   * Every `useReplaceDocument` write. `useIsReplacingDocument` narrows it to the documents
   * one touches (its target, or a swap's source) through the mutation's variables, so no
   * caller keeps its own in-flight flag (#609).
   */
  replaceWrite: () => ['documents', 'replace-write'] as const,
} as const;

/**
 * Whether a cached key is one of the audit's document LISTS: `list(auditId, params)`, bare or
 * filtered. `lists(auditId)` is also the prefix of `metaTypes(auditId)`, whose data is not a
 * list, so a cache WRITE of list data narrows its prefix match with this (DATA_FLOW.md,
 * "A prefix is for invalidation"; #875).
 */
export const isDocumentListKey = (queryKey: readonly unknown[], auditId?: string): boolean =>
  queryKey[0] === 'documents' &&
  queryKey[1] === auditId &&
  (queryKey.length === 2 ||
    (queryKey.length === 3 && typeof queryKey[2] === 'object' && queryKey[2] !== null));
